import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { Icon } from '~/components';
import { cn } from '~/lib/cn';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/modules/artist-roster/components/Table';
import { SkeletonTable } from '~/modules/artist-roster/components/Table/SkeletonTable';

import { useBenchmarkColumns } from '../hooks/useBenchmarkColumns';
import type { BenchmarkTableMetaType, CampaignBenchmark } from '../lib/types';

interface CampaignBenchmarksTableProps {
  data: CampaignBenchmark[];
  error?: Error | null;
  isLoadingBenchmarks: boolean;
  isFetchingBenchmarks: boolean;
  className?: string;
}

export function CampaignBenchmarksTable({
  data,
  error,
  isFetchingBenchmarks,
  className,
}: CampaignBenchmarksTableProps) {
  const { t } = useTranslation('campaign-planner', { keyPrefix: 'benchmarks' });

  const { columns } = useBenchmarkColumns();

  const table = useReactTable({
    data: data || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (error) {
    return (
      <div className="flex w-full flex-col items-center justify-center gap-lg rounded-sm py-5xl tracking-wider outline-dashed outline-1 outline-offset-[-1px] outline-emphasis-subtle">
        <span className="flex shrink-0 items-center justify-center">
          <Icon
            type="waveform"
            size="4x"
            variant="light"
            aria-hidden
            className="text-default"
          />
        </span>
        <div className="flex w-full flex-col items-center justify-center gap-sm">
          <p className="text-body-lg font-semibold text-white">
            {t('errors.generic')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <table
        aria-label={t('labels.benchmarksHeaders')}
        className={cn(
          'w-full table-fixed border-separate border-spacing-x-0 border-spacing-y-xs text-left',
          className,
        )}
      >
        <TableHeader className="sticky top-0">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const columnMeta = header.column.columnDef
                  .meta as BenchmarkTableMetaType;

                return (
                  <TableHead
                    key={header.id}
                    className={cn(
                      'text-body-sm font-normal first:pl-lg',
                      columnMeta?.className,
                    )}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
      </table>
      <Table
        aria-label={t('labels.benchmarksList')}
        className={cn('w-full flex-1 table-fixed text-left', className)}
      >
        <TableBody>
          {isFetchingBenchmarks ? (
            <SkeletonTable
              size={4}
              headers={table.getHeaderGroups()[0].headers}
              cellClassName="h-[5rem]"
              firstLineClassName="w-4xl"
              secondLineClassName="w-3xl"
            />
          ) : table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row, rowIndex) => (
              <TableRow
                aria-label={`Row for ${row.original?.market || 'benchmark'}`}
                key={row.id}
                data-state={row.getIsSelected() && 'selected'}
                className="group relative duration-300"
              >
                {row.getVisibleCells().map((cell) => {
                  const columnMeta = cell.column.columnDef
                    .meta as BenchmarkTableMetaType;
                  return (
                    <TableCell
                      key={cell.id}
                      className={cn(
                        'border-y p-0 border-transparent',
                        'first:rounded-l-sm first:border-l',
                        'last:rounded-r-sm last:border-r',
                        rowIndex % 2 === 0 && 'bg-default-inverse/default',
                        columnMeta?.className,
                      )}
                    >
                      {cell.column.id === 'market' ? (
                        <div className="flex items-center gap-sm py-md pl-lg">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </div>
                      ) : (
                        flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={10}>
                <div className="mt-md flex w-full flex-col items-center justify-center gap-lg rounded-sm py-5xl tracking-wider outline-dashed outline-1 outline-offset-[-1px] outline-emphasis-subtle">
                  <span className="flex shrink-0 items-center justify-center">
                    <Icon
                      type="user-music"
                      size="4x"
                      variant="light"
                      aria-hidden
                      className="text-default"
                    />
                  </span>
                  <div className="flex w-full flex-col items-center justify-center gap-sm">
                    <p className="text-body-lg font-semibold text-white">
                      {t('noData.title')}
                    </p>
                    <p className="text-body-md font-normal text-subtle">
                      {t('noData.sub')}
                    </p>
                  </div>
                </div>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

CampaignBenchmarksTable.displayName = 'CampaignBenchmarksTable';
